import React, { useState, useEffect } from 'react';
import '../AssessmentForm.css';

const reportID = '050B407B-7499-4182-8F80-4A341822C69C'; // Placeholder for report ID if needed

const ClinicalNotesTab = ({ reportId, api, onSubmit, onSave , isPublished}) => {
  const [formData, setFormData] = useState({
    agenda: '',
    sessionNotes: '',
    homeworkReview: '',
    medicalHistoryDescription: ''
  });
  const [savingStates, setSavingStates] = useState({});
  const [saveTimeouts, setSaveTimeouts] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const fieldMapping = {
    agenda: 'agenda',
    sessionNotes: 'session_notes',
    homeworkReview: 'homework_review',
    medicalHistoryDescription: 'medical_history_description'
  };

  // Load existing data from API
  useEffect(() => {
    const loadExistingData = async () => {
      try {
        setLoading(true);
        const effectiveReportId = reportId || reportID;
        const response = await api.webAPIFetch(`/api/PatientCaseReports/${effectiveReportId}/sections/clinical-notes`);

        if (response.ok) {
          const data = await response.json();

          setFormData({
            agenda: data.agenda || '',
            sessionNotes: data.sessionNotes || '',
            homeworkReview: data.homeworkReview || '',
            medicalHistoryDescription: data.medicalHistoryDescription || ''
          });

          // Mark all loaded fields as saved
          const savedStates = {};
          Object.keys(fieldMapping).forEach(field => {
            if (data[field]) {
              savedStates[field] = 'saved';
            }
          });
          setSavingStates(savedStates);
        }
      } catch (error) {
        console.error('Error loading clinical notes:', error);
        setLoadError(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadExistingData();
  }, [reportId]);

  // API call for saving individual field
  const saveField = async (fieldName, value) => {
    if (isPublished) return;
    console.log(`Saving Clinical Notes field: ${fieldName}, Value length: ${value?.length || 0}`);

    const apiFieldName = fieldMapping[fieldName];
    const effectiveReportId = reportId || reportID;

    try {
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${effectiveReportId}/sections/clinical-notes/fields/${apiFieldName}`, {
        method: 'PATCH',
        body: JSON.stringify({
          value: value
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setSavingStates(prev => ({
        ...prev,
        [fieldName]: 'saved'
      }));

      if (onSave) {
        onSave();
      }

    } catch (error) {
      console.error(`Error saving field ${fieldName}:`, error);
      setSavingStates(prev => ({
        ...prev,
        [fieldName]: 'error'
      }));
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));

    // Clear any existing timeout for this field
    if (saveTimeouts[fieldName]) {
      clearTimeout(saveTimeouts[fieldName]);
    }

    // Set saving state immediately
    setSavingStates(prev => ({
      ...prev,
      [fieldName]: 'saving'
    }));

    // Debounce the API call
    const timeoutId = setTimeout(() => {
      saveField(fieldName, value);
    }, 1000); // Longer delay for larger text fields

    setSaveTimeouts(prev => ({
      ...prev,
      [fieldName]: timeoutId
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const result = {
      sectionType: 'ClinicalNotes',
      ...formData,
      completedAt: new Date().toISOString()
    };

    if (onSubmit) {
      await onSubmit(result);
    }
  };

  const getSaveStatus = (fieldName) => {
    const state = savingStates[fieldName];
    if (state === 'saving') {
      return <div className="save-status saving inline"><div className="spinner"></div></div>;
    }
    if (state === 'saved') {
      return <div className="save-status saved inline"><span className="checkmark">✓</span></div>;
    }
    if (state === 'error') {
      return <div className="save-status error inline"><span className="error-mark">⚠</span></div>;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="assessment-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading clinical notes...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="assessment-container">
        <div className="error-state">
          <p>Error loading clinical notes: {loadError}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="assessment-container">
      <fieldset disabled={isPublished} style={{ border: "none", padding: 0, margin: 0 }}>
      <div className="assessment-header">
        <h2>Clinical Notes</h2>
        <p>Document the clinical session details, agenda, notes, homework review, and medical history.</p>
      </div>

      <form onSubmit={handleSubmit} className="assessment-form clinical-notes-form">
        <div className="form-fields">
          <div className="form-field">
            <label htmlFor="agenda">
              Session Agenda
              {getSaveStatus('agenda')}
            </label>
            <textarea
              id="agenda"
              value={formData.agenda}
              onChange={(e) => handleFieldChange('agenda', e.target.value)}
              placeholder="Enter the session agenda and topics to be covered"
              className="form-textarea"
              rows="3"
            />
          </div>

          <div className="form-field">
            <label htmlFor="sessionNotes">
              Clinical Session Notes
              {getSaveStatus('sessionNotes')}
            </label>
            <textarea
              id="sessionNotes"
              value={formData.sessionNotes}
              onChange={(e) => handleFieldChange('sessionNotes', e.target.value)}
              placeholder="Enter detailed clinical session notes including observations, interventions, and patient responses"
              className="form-textarea"
              rows="8"
            />
          </div>

          <div className="form-field">
            <label htmlFor="homeworkReview">
              Homework Review
              {getSaveStatus('homeworkReview')}
            </label>
            <textarea
              id="homeworkReview"
              value={formData.homeworkReview}
              onChange={(e) => handleFieldChange('homeworkReview', e.target.value)}
              placeholder="Review any homework assignments from previous sessions and patient's completion"
              className="form-textarea"
              rows="4"
            />
          </div>

          <div className="form-field">
            <label htmlFor="medicalHistoryDescription">
              Medical History Description
              {getSaveStatus('medicalHistoryDescription')}
            </label>
            <textarea
              id="medicalHistoryDescription"
              value={formData.medicalHistoryDescription}
              onChange={(e) => handleFieldChange('medicalHistoryDescription', e.target.value)}
              placeholder="Document relevant medical history information"
              className="form-textarea"
              rows="5"
            />
          </div>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="submit-button"
          >
            Save Clinical Notes
          </button>
        </div>
      </form>

      <style jsx>{`
        .clinical-notes-form .form-fields {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .form-field {
          display: flex;
          flex-direction: column;
        }

        .form-field label {
          font-weight: 600;
          margin-bottom: 0.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .form-textarea {
          padding: 0.75rem;
          border: 1px solid #ccc;
          border-radius: 4px;
          font-size: 1rem;
          font-family: inherit;
          resize: vertical;
          line-height: 1.5;
        }

        .form-textarea:focus {
          outline: none;
          border-color: #4a90e2;
          box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.1);
        }

        .save-status.inline {
          display: inline-flex;
          margin-left: 0.5rem;
        }

        .save-status.inline .spinner {
          width: 16px;
          height: 16px;
        }
      `}</style>
      </fieldset>

    </div>
  );
};

export default ClinicalNotesTab;
